import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Loader2, Plus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/** Post-purchase growth panels: referral code + one-click 30%-off spoofer upsell. */
export default function PostPurchase({ sessionId, order, productSlug }) {
  const [spoofer, setSpoofer] = useState(null);
  const [busy, setBusy] = useState(false);

  const referral = order?.referral_code;
  const showUpsell = productSlug !== "hwid-spoofer";

  useEffect(() => {
    if (showUpsell) {
      api.get("/products/hwid-spoofer").then((r) => setSpoofer(r.data)).catch(() => {});
    }
  }, [showUpsell]);

  const buyUpsell = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/upsell/${sessionId}`);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upsell unavailable");
      setBusy(false);
    }
  };

  const copy = (k) => { navigator.clipboard.writeText(k); toast.success("Code copied"); };

  if (!referral && !showUpsell) return null;

  const monthTier = spoofer?.tiers?.find((t) => t.tier === "month") || spoofer?.tiers?.[0];
  const upsellPrice = monthTier ? (monthTier.price * 0.7).toFixed(2) : null;

  return (
    <div className="mt-6 space-y-4 text-left">
      {referral && (
        <div className="rounded-2xl border border-white/15 p-5" data-testid="referral-panel">
          <div className="font-mono text-[10px] tracking-[0.2em] text-white/40">EARN WITH GZ</div>
          <div className="mt-3 flex items-center gap-3">
            <code className="font-mono text-lg tracking-[0.15em] text-white" data-testid="referral-code">{referral}</code>
            <button onClick={() => copy(referral)} data-testid="copy-referral-btn" className="text-white/60 hover:text-white" title="Copy code"><Copy size={16} /></button>
          </div>
          <p className="mt-2 text-xs text-white/50 leading-relaxed">
            Friends get 5% off with your code — you earn 10% store credit on every sale. Credit auto-applies to your next order.
          </p>
        </div>
      )}
      {showUpsell && spoofer && (
        <div className="rounded-2xl border border-white/30 p-5 bg-white/[0.02]" data-testid="upsell-panel">
          <div className="font-mono text-[10px] tracking-[0.2em] text-white/40">COMPLETE THE STACK — ONE-CLICK 30% OFF</div>
          <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-display text-xl uppercase leading-none">{spoofer.name}</div>
              <div className="mt-1.5 font-mono text-xs text-white/50">
                1 MONTH <span className="line-through">${monthTier.price.toFixed(2)}</span>{" "}
                <span className="text-white">${upsellPrice}</span>
              </div>
            </div>
            <button onClick={buyUpsell} disabled={busy} data-testid="upsell-btn"
              className="btn-inj px-5 py-3 text-xs inline-flex items-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} ADD FOR ${upsellPrice}
            </button>
          </div>
          <p className="mt-2 font-mono text-[10px] tracking-[0.12em] text-white/35">// SAME EMAIL, SKIP THE FORMS — STRAIGHT TO PAYMENT</p>
        </div>
      )}
    </div>
  );
}
